/**
 * Progressive email voice formatting (V1.5 conversational single-email review).
 */

import {
  prepareEmailBodyForSpeech,
  takeSpokenExcerpt,
} from "./prepareEmailBodyForSpeech";

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
];

export const EMAIL_PAGE_SIZE = 5;

export const OPEN_INBOX_OFFER = "Would you like me to open your inbox?";

export const OPEN_SPAM_OFFER = "Would you like me to open your Spam folder?";

export const REVIEW_SPAM_OFFER = "Would you like me to review those Spam messages?";

export const EMAIL_WHAT_NEXT = "What would you like to do?";

export const EMAIL_WHAT_NEXT_AFTER = "What would you like to do next?";

export const FULL_EMAIL_READ_OFFER = "Would you like me to read the full email?";

export const REVIEW_ALL_OFFER = EMAIL_WHAT_NEXT;

const SHORT_SUBJECT_MAX = 42;

function numberToWords(n) {
  if (n >= 0 && n < NUMBER_WORDS.length) {
    return NUMBER_WORDS[n];
  }
  return String(n);
}

export function stripNoiseSubject(subject) {
  return String(subject || "")
    .replace(/^(re|fw|fwd)\s*:\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Short subject for identification only (not a summary).
 * @param {string} subject
 * @param {number} [maxLen]
 */
export function formatShortSubject(subject, maxLen = SHORT_SUBJECT_MAX) {
  const cleaned = stripNoiseSubject(subject);
  if (!cleaned || cleaned === "(no subject)") {
    return "No subject";
  }
  if (cleaned.length <= maxLen) {
    return cleaned;
  }
  const sliced = cleaned.slice(0, maxLen);
  const lastSpace = sliced.lastIndexOf(" ");
  const base = (lastSpace > 12 ? sliced.slice(0, lastSpace) : sliced).trim();
  return `${base}…`;
}

function senderName(message) {
  return message.fromName || message.from || "someone";
}

export function getEmailHighlightCount(totalUnread) {
  const total = Math.max(0, Number(totalUnread) || 0);
  return Math.min(total, EMAIL_PAGE_SIZE);
}

export function getEmailHighlightMessages(messages) {
  const list = Array.isArray(messages) ? messages : [];
  return list.slice(0, getEmailHighlightCount(list.length));
}

export function getEmailPage(messages, offset = 0, pageSize = EMAIL_PAGE_SIZE) {
  const list = Array.isArray(messages) ? messages : [];
  const start = Math.max(0, Number(offset) || 0);
  const size = Math.max(1, Number(pageSize) || EMAIL_PAGE_SIZE);
  return list.slice(start, start + size);
}

export function hasEmailNextPage(messages, offset = 0, pageSize = EMAIL_PAGE_SIZE) {
  const list = Array.isArray(messages) ? messages : [];
  const start = Math.max(0, Number(offset) || 0);
  const size = Math.max(1, Number(pageSize) || EMAIL_PAGE_SIZE);
  return start + size < list.length;
}

export function formatSpamReviewLine(spam) {
  const count = Math.max(0, Number(spam?.unreadCount) || 0);
  if (count <= 0) {
    return "I also checked Spam — nothing unread there.";
  }
  if (count === 1) {
    return "I also noticed one message in Spam that may be worth a quick look.";
  }
  if (count === 2) {
    return "I also noticed two messages in Spam that may be worth a quick look.";
  }
  return `I also noticed ${numberToWords(count)} messages in Spam that may be worth a quick look.`;
}

function formatSenderList(names) {
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Numbered sender + short subject lines for the active page.
 * @param {Array<object>} pageMessages
 * @param {number} [startNumber] 1-based starting index (for later pages use offset+1)
 */
export function formatNumberedEmailList(pageMessages, startNumber = 1) {
  const page = Array.isArray(pageMessages) ? pageMessages : [];
  const start = Math.max(1, Number(startNumber) || 1);
  return page
    .map((message, index) => {
      const name = senderName(message);
      const subject = formatShortSubject(message.subject);
      return `${start + index}. ${name} — ${subject}`;
    })
    .join("\n");
}

/**
 * Concise conversational offer — Ashley Home discovers capabilities.
 * @param {{ afterAction?: boolean }} [options]
 */
export function formatEmailBrowseOffer(options = {}) {
  return options.afterAction ? EMAIL_WHAT_NEXT_AFTER : EMAIL_WHAT_NEXT;
}

export function formatEmailBrowseReprompt() {
  return EMAIL_WHAT_NEXT;
}

/**
 * Sender + short subject list for a page (newest-first).
 * @param {Array<object>} pageMessages
 * @param {{ isFirstPage?: boolean, totalUnread?: number, offset?: number }} [options]
 */
export function formatEmailSenderPage(pageMessages, options = {}) {
  const page = Array.isArray(pageMessages) ? pageMessages : [];
  const count = page.length;
  if (count === 0) {
    return "There are no more unread emails in your Primary inbox.";
  }

  const isFirstPage = options.isFirstPage !== false;
  const totalUnread = Number(options.totalUnread);
  const offset = Math.max(0, Number(options.offset) || 0);
  const listBlock = formatNumberedEmailList(page, offset + 1);

  if (isFirstPage) {
    if (count === 1) {
      return `Your most recent unread email is:\n${listBlock}`;
    }
    if (Number.isFinite(totalUnread) && totalUnread > EMAIL_PAGE_SIZE) {
      return `Your ${numberToWords(count)} most recent unread emails are:\n${listBlock}`;
    }
    return `Your most recent unread emails are:\n${listBlock}`;
  }

  if (count === 1) {
    return `The remaining unread email is:\n${listBlock}`;
  }
  if (count < EMAIL_PAGE_SIZE) {
    return `The remaining unread emails are:\n${listBlock}`;
  }
  return `The next five unread emails are:\n${listBlock}`;
}

/**
 * Full unread sender list (no content summaries) — Review All Unread.
 */
export function formatAllUnreadSenders(messages) {
  const list = Array.isArray(messages) ? messages : [];
  if (list.length === 0) {
    return `You have no unread emails in your Primary inbox. ${OPEN_INBOX_OFFER}`;
  }

  const names = list.map((m) => senderName(m));
  if (list.length === 1) {
    return `Your unread email is from ${names[0]}. ${REVIEW_ALL_OFFER}`;
  }
  return `Your unread emails are from ${formatSenderList(names)}. ${REVIEW_ALL_OFFER}`;
}

export function formatReviewAllReprompt() {
  return EMAIL_WHAT_NEXT;
}

/**
 * Spam attention intro (rule-based flags only).
 */
export function formatSpamAttentionIntro(flaggedCount) {
  const count = Math.max(0, Number(flaggedCount) || 0);
  if (count <= 0) {
    return "I didn't find any Spam messages that appear to require your attention.";
  }
  if (count === 1) {
    return `I found one Spam message that may deserve your attention. ${REVIEW_SPAM_OFFER}`;
  }
  if (count === 2) {
    return `I found two Spam messages that may deserve your attention. ${REVIEW_SPAM_OFFER}`;
  }
  return `I found ${numberToWords(count)} Spam messages that may deserve your attention. ${REVIEW_SPAM_OFFER}`;
}

/**
 * High-level overview: count + first page of numbered emails + browse offer.
 */
export function formatEmailSenderOverview(messages, options = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const total = list.length;
  const spamLine = formatSpamReviewLine(options.spamReview);

  if (total === 0) {
    return `You're all caught up — no unread emails in your Primary inbox. ${spamLine} ${OPEN_INBOX_OFFER}`;
  }

  const page = getEmailPage(list, 0, EMAIL_PAGE_SIZE);
  const countPhrase = numberToWords(total);
  const emailWord = total === 1 ? "email" : "emails";
  const intro = `You have ${countPhrase} unread ${emailWord} in your Primary inbox.`;
  const senderClause = formatEmailSenderPage(page, {
    isFirstPage: true,
    totalUnread: total,
    offset: 0,
  });
  const offer = formatEmailBrowseOffer();

  return `${intro}\n\n${senderClause}\n\n${spamLine} ${offer}`;
}

function describeMessageAtIndex(message, index) {
  const subject = stripNoiseSubject(message.subject);
  const name = senderName(message);
  const regarding =
    !subject || subject === "(no subject)" ? "" : ` regarding ${subject}`;

  if (index === 0) {
    return `The newest is from ${name}${regarding}`;
  }
  if (index === 1) {
    return `Next is from ${name}${regarding}`;
  }
  return `Then one from ${name}${regarding}`;
}

/** Batch page summary (Level 2 → subject-level, not full single-email). */
export function formatEmailDetailSummary(highlightMessages) {
  const list = Array.isArray(highlightMessages) ? highlightMessages : [];
  if (list.length === 0) {
    return "I don't have any emails to summarize right now.";
  }

  const parts = list.map((m, index) => describeMessageAtIndex(m, index));
  return `${parts.join(". ")}.`;
}

/**
 * Richer single-email summary (Level 3). Uses body excerpt when available.
 * @param {object} message
 * @param {string} [bodyText]
 */
export function formatSingleEmailRichSummary(message, bodyText) {
  const name = senderName(message);
  const subject = stripNoiseSubject(message.subject);
  const source =
    bodyText && String(bodyText).trim()
      ? prepareEmailBodyForSpeech(bodyText)
      : prepareEmailBodyForSpeech(message?.snippet || "");
  const excerpt = takeSpokenExcerpt(source, 280);

  if (!excerpt) {
    if (subject && subject !== "(no subject)") {
      return `The email from ${name} is about ${subject}. ${FULL_EMAIL_READ_OFFER}`;
    }
    return `The email from ${name} doesn't include much readable content. ${FULL_EMAIL_READ_OFFER}`;
  }

  const topic =
    subject && subject !== "(no subject)"
      ? `The email from ${name} covers ${subject}. `
      : `The email from ${name}: `;

  return `${topic}${excerpt} ${FULL_EMAIL_READ_OFFER}`;
}

/**
 * Full spoken email (Level 4).
 * @param {object} message
 * @param {string} bodyText
 */
export function formatFullEmailSpeech(message, bodyText) {
  const name = senderName(message);
  const cleaned = prepareEmailBodyForSpeech(bodyText || message?.snippet || "");
  if (!cleaned) {
    return `That's all I could read from ${name}'s email. ${EMAIL_WHAT_NEXT_AFTER}`;
  }
  return `${cleaned} ${EMAIL_WHAT_NEXT_AFTER}`;
}

/**
 * @param {string} senderDisplay
 * @param {number} count
 */
export function formatAmbiguousSenderClarification(senderDisplay, count) {
  const n = Math.max(2, Number(count) || 2);
  const name = senderDisplay || "that sender";
  const countPhrase = numberToWords(n);
  return `I found ${countPhrase} unread emails from ${name}. Would you like the newest one or the older one?`;
}

export function formatEmailNotFoundOnPage() {
  return `I couldn't find that email on this page. ${EMAIL_WHAT_NEXT}`;
}

/** @deprecated Prefer formatEmailSenderOverview / formatEmailDetailSummary. */
export function formatEmailVoiceSummary(messages, options = {}) {
  return formatEmailSenderOverview(messages, options);
}
